<script>
import Layout from "../../layouts/auth";

import {
  authFackMethods,
  notificationMethods,
} from "@/state/helpers";
import { mapState } from "vuex";

import appConfig from "@/app.config";
import { required, requiredIf } from "vuelidate/lib/validators";

export default {
  page: {
    title: "Giriş Yap",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  components: {
    Layout,
  },
  data() {
    return {
      email: "",
      password: "",
      phonenumber: "",
      submitted: false,
      authError: null,
      tryingToLogIn: false,
      isAuthError: false,
      siteKey: process.env.VUE_APP_RECAPTCHA_SITE_KEY,
      recaptchaReady: false,
      forgotPasswordToggle: false,
      passwordVisible: false,
    };
  },
  validations: {
    email: { required },
    password: {
      required: requiredIf(function () {
        return !this.forgotPasswordToggle
      })
    },
    phonenumber: {
      required: requiredIf(function () {
        return this.forgotPasswordToggle
      })
    },
  },
  computed: {
    ...mapState("authfack", ["loading"]),
    notification() {
      return this.$store ? this.$store.state.notification : null;
    },
  },
  mounted() {
    this.loadRecaptcha();
  },
  methods: {
    ...authFackMethods,
    ...notificationMethods,

    loadRecaptcha() {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => { this.recaptchaReady = true; });
        return;
      }
      if (document.getElementById('recaptcha-v3-script')) return;

      const script = document.createElement('script');
      script.id = 'recaptcha-v3-script';
      script.src = `https://www.google.com/recaptcha/api.js?render=${this.siteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.grecaptcha.ready(() => { this.recaptchaReady = true; });
      };
      document.head.appendChild(script);
    },

    getRecaptchaToken(action) {
      return new Promise((resolve, reject) => {
        if (!window.grecaptcha) {
          reject(new Error('recaptcha_not_loaded'));
          return;
        }
        window.grecaptcha.ready(() => {
          window.grecaptcha.execute(this.siteKey, { action })
            .then(resolve)
            .catch(reject);
        });
      });
    },

    async tryToLogIn() {
      this.submitted = true;
      this.loadingStatus(true);

      this.$v.$touch();
      if (this.$v.$invalid) {
        this.loadingStatus(false);
        return;
      }

      let recaptcha_token;
      try {
        const action = this.forgotPasswordToggle ? 'forgot_password' : 'login';
        recaptcha_token = await this.getRecaptchaToken(action);
      } catch (e) {
        this.loadingStatus(false);
        this.$store.dispatch('notification/error', 'Güvenlik doğrulaması yüklenemedi. Sayfayı yenileyin.');
        return;
      }

      this.tryingToLogIn = true;
      this.authError = null;
      this.submitted = false;

      if (this.forgotPasswordToggle) {
        this.forgotPassword({
          email: this.email,
          phonenumber: this.phonenumber,
          recaptcha_token,
        });
      } else {
        this.login({
          email: this.email,
          password: this.password,
          recaptcha_token,
        });
      }
    },
    togglePasswordVisibility() {
      this.passwordVisible = !this.passwordVisible;
    },
  },
};
</script>

<template>
  <Layout>
    <div class="login-saas">
      <div class="login-saas__brand">
        <div class="login-saas__brand-inner">
          <div class="login-saas__logo-wrap">
            <img src="@/assets/images/logo.png" alt="Logo" class="login-saas__logo" />
          </div>
          <h1 class="login-saas__headline">Operasyonlarınızı<br />tek merkezden yönetin</h1>
          <p class="login-saas__tagline">
            Abone yönetimi, tahsilat takibi ve operasyon analitiği — güvenli ve ölçeklenebilir altyapı.
          </p>
          <ul class="login-saas__features">
            <li><i class="bx bx-shield-quarter"></i><span>256-bit güvenli oturum</span></li>
            <li><i class="bx bx-line-chart"></i><span>Gerçek zamanlı operasyon paneli</span></li>
            <li><i class="bx bx-cloud"></i><span>Bulut tabanlı kurumsal erişim</span></li>
          </ul>
        </div>
        <div class="login-saas__brand-glow" aria-hidden="true"></div>
      </div>

      <div class="login-saas__form-side">
        <div class="login-saas__card">
          <div class="login-saas__card-header">
            <div class="login-saas__mobile-logo">
              <img src="@/assets/images/logo.png" alt="Logo" />
            </div>
            <h2 class="login-saas__card-title">
              {{ forgotPasswordToggle ? 'Şifre Sıfırlama' : 'Hoş Geldiniz' }}
            </h2>
            <p class="login-saas__card-sub">
              {{ forgotPasswordToggle ? 'Kayıtlı bilgilerinizle yeni şifre talep edin' : 'Hesabınıza giriş yapın' }}
            </p>
          </div>

          <b-alert
            v-model="isAuthError"
            variant="danger"
            class="login-saas__alert"
            dismissible
          >{{ authError }}</b-alert>

          <div
            v-if="notification.message"
            :class="'alert login-saas__alert ' + notification.type"
          >{{ notification.message }}</div>

          <b-form class="login-saas__form" @submit.prevent="tryToLogIn">
            <div class="login-saas__field">
              <label for="username" class="login-saas__label">Kullanıcı Adı</label>
              <div class="login-saas__input-wrap">
                <i class="bx bx-user login-saas__input-icon"></i>
                <input
                  type="text"
                  v-model="email"
                  class="login-saas__input"
                  id="username"
                  placeholder="Kullanıcı adınızı girin"
                  :class="{ 'is-invalid': submitted && $v.email.$error }"
                />
              </div>
              <div v-if="submitted && $v.email.$error" class="login-saas__error">
                <span v-if="!$v.email.required">Kullanıcı adı alanı zorunludur.</span>
              </div>
            </div>

            <div class="login-saas__field" v-if="!forgotPasswordToggle">
              <label for="password" class="login-saas__label">Şifre</label>
              <div class="login-saas__input-wrap">
                <i class="bx bx-lock-alt login-saas__input-icon"></i>
                <input
                  :type="passwordVisible ? 'text' : 'password'"
                  v-model="password"
                  class="login-saas__input login-saas__input--pwd"
                  id="password"
                  placeholder="Şifrenizi girin"
                  :class="{ 'is-invalid': submitted && $v.password.$error }"
                />
                <button type="button" class="login-saas__toggle-pwd" @click="togglePasswordVisibility" tabindex="-1">
                  <i :class="passwordVisible ? 'bx bx-hide' : 'bx bx-show'"></i>
                </button>
              </div>
              <div v-if="submitted && $v.password.$error" class="login-saas__error">
                <span v-if="!$v.password.required">Şifre alanı zorunludur.</span>
              </div>
            </div>

            <div class="login-saas__field" v-if="forgotPasswordToggle">
              <label for="phonenumber" class="login-saas__label">Telefon Numarası</label>
              <div class="login-saas__input-wrap">
                <i class="bx bx-phone login-saas__input-icon"></i>
                <input
                  type="text"
                  v-model="phonenumber"
                  class="login-saas__input"
                  id="phonenumber"
                  placeholder="Kayıtlı telefon numaranız"
                  :class="{ 'is-invalid': submitted && $v.phonenumber.$error }"
                />
              </div>
              <div v-if="submitted && $v.phonenumber.$error" class="login-saas__error">
                <span v-if="!$v.phonenumber.required">Telefon numarası alanı zorunludur.</span>
              </div>
            </div>

            <div class="login-saas__notice" v-if="forgotPasswordToggle">
              <i class="bx bx-info-circle"></i>
              Kayıtlı telefon numaranıza yeni şifreniz gönderilecektir.
            </div>

            <div class="login-saas__links">
              <a
                href="javascript:void(0)"
                v-if="!forgotPasswordToggle"
                @click="forgotPasswordToggle = !forgotPasswordToggle"
              >Şifremi Unuttum</a>
              <a
                href="javascript:void(0)"
                v-else
                @click="forgotPasswordToggle = !forgotPasswordToggle"
              ><i class="bx bx-arrow-back"></i> Girişe Dön</a>
            </div>

            

            <button
              type="submit"
              class="login-saas__submit"
              :disabled="loading"
            >
              <span v-if="loading">
                <i class="bx bx-loader bx-spin"></i> Bekleyiniz
              </span>
              <span v-else>
                <template v-if="!forgotPasswordToggle">Giriş Yap</template>
                <template v-else>SMS Gönder</template>
                <i class="bx bx-right-arrow-alt"></i>
              </span>
            </button>
          </b-form>

          <p class="login-saas__recaptcha-badge mt-3">
              Bu site reCAPTCHA v3 ile korunmaktadır.
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener">Gizlilik</a>
              ·
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener">Şartlar</a>
            </p>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.login-saas {
  display: flex;
  min-height: 100vh;
  width: 100%;
}

.login-saas__brand {
  position: relative;
  flex: 1;
  display: none;
  align-items: center;
  justify-content: center;
  padding: 48px 56px;
  overflow: hidden;
}

.login-saas__brand-inner {
  position: relative;
  z-index: 1;
  max-width: 480px;
}

.login-saas__brand-glow {
  position: absolute;
  width: 360px;
  height: 360px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(6, 182, 212, 0.15), transparent 70%);
  top: 30%;
  left: 20%;
  filter: blur(40px);
  pointer-events: none;
}

.login-saas__logo-wrap {
  margin-bottom: 32px;
}

.login-saas__logo {
  max-width: 200px;
  height: auto;
  filter: brightness(0) invert(1);
  opacity: 0.95;
}

.login-saas__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #67e8f9;
  margin-bottom: 16px;
}

.login-saas__pulse {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22d3ee;
  box-shadow: 0 0 10px #22d3ee;
  animation: loginPulse 2s ease-in-out infinite;
}

.login-saas__headline {
  font-size: 2.25rem;
  font-weight: 700;
  color: #f8fafc;
  line-height: 1.2;
  letter-spacing: -0.02em;
  margin-bottom: 16px;
}

.login-saas__tagline {
  font-size: 0.95rem;
  color: rgba(203, 213, 225, 0.85);
  line-height: 1.65;
  margin-bottom: 32px;
}

.login-saas__features {
  list-style: none;
  padding: 0;
  margin: 0 0 36px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.login-saas__features li {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.9rem;
  color: rgba(226, 232, 240, 0.9);
}

.login-saas__features i {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(6, 182, 212, 0.12);
  border: 1px solid rgba(34, 211, 238, 0.2);
  color: #67e8f9;
  font-size: 18px;
  flex-shrink: 0;
}

.login-saas__stats {
  display: flex;
  gap: 24px;
  padding-top: 28px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.login-saas__stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.login-saas__stat strong {
  font-size: 1.25rem;
  font-weight: 700;
  color: #f8fafc;
}

.login-saas__stat span {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(148, 163, 184, 0.9);
}

.login-saas__form-side {
  flex: 0 0 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
}

.login-saas__card {
  width: 100%;
  max-width: 440px;
  padding: 36px 32px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.97);
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow:
    0 32px 64px rgba(0, 0, 0, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
}

.login-saas__card-header {
  margin-bottom: 28px;
  text-align: center;
}

.login-saas__mobile-logo {
  display: none;
  margin-bottom: 20px;
}

.login-saas__mobile-logo img {
  max-width: 160px;
  height: auto;
}

.login-saas__card-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
  letter-spacing: -0.02em;
}

.login-saas__card-sub {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
}

.login-saas__alert {
  margin-bottom: 16px;
  border-radius: 12px;
  font-size: 0.875rem;
}

.login-saas__form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.login-saas__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.login-saas__label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #334155;
}

.login-saas__input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.login-saas__input-icon {
  position: absolute;
  left: 14px;
  font-size: 18px;
  color: #94a3b8;
  pointer-events: none;
  z-index: 1;
}

.login-saas__input {
  width: 100%;
  height: 48px;
  padding: 0 14px 0 44px;
  font-size: 0.9375rem;
  color: #0f172a;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
}

.login-saas__input--pwd {
  padding-right: 44px;
}

.login-saas__input::placeholder {
  color: #94a3b8;
}

.login-saas__input:focus {
  background: #fff;
  border-color: #0891b2;
  box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.12);
}

.login-saas__input.is-invalid {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.login-saas__toggle-pwd {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  padding: 4px;
  color: #94a3b8;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  transition: color 0.2s;
}

.login-saas__toggle-pwd:hover {
  color: #0891b2;
}

.login-saas__error {
  font-size: 0.8125rem;
  color: #ef4444;
}

.login-saas__notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  font-size: 0.8125rem;
  line-height: 1.5;
}

.login-saas__notice i {
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 1px;
}

.login-saas__links {
  text-align: right;
}

.login-saas__links a {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0891b2;
  text-decoration: none;
  transition: color 0.2s;
}

.login-saas__links a:hover {
  color: #0e7490;
}

.login-saas__links a i {
  vertical-align: -2px;
  margin-right: 4px;
}

.login-saas__recaptcha-badge {
  margin: 0;
  text-align: center;
  font-size: 0.7rem;
  color: #94a3b8;
  line-height: 1.5;
}

.login-saas__recaptcha-badge a {
  color: #64748b;
  text-decoration: none;
}

.login-saas__recaptcha-badge a:hover {
  color: #0891b2;
}

.login-saas__submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 50px;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #0891b2 0%, #2563eb 100%);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s;
  box-shadow: 0 8px 24px rgba(6, 182, 212, 0.35);
}

.login-saas__submit:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 12px 32px rgba(6, 182, 212, 0.45);
}

.login-saas__submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.login-saas__submit i.bx-right-arrow-alt {
  font-size: 20px;
}

.login-saas__footer {
  margin: 24px 0 0;
  padding-top: 20px;
  border-top: 1px solid #f1f5f9;
  text-align: center;
  font-size: 0.75rem;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

@keyframes loginPulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.85); }
}

@media (min-width: 992px) {
  .login-saas__brand {
    display: flex;
    flex: 0 0 48%;
    border-right: 1px solid rgba(255, 255, 255, 0.06);
  }

  .login-saas__form-side {
    flex: 0 0 52%;
    padding: 48px 56px;
  }
}

@media (max-width: 991.98px) {
  .login-saas__mobile-logo {
    display: block;
  }

  .login-saas__card {
    padding: 28px 24px;
  }

  .login-saas__card-header {
    margin-bottom: 22px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .login-saas__pulse {
    animation: none !important;
  }
}
</style>
